// Age gate overlay — see src/lib/ageGate.ts for the full policy rationale
// (Soap card-brand review; unauthenticated web only; never on native).
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ShieldAlert } from 'lucide-react';
import {
  shouldShowAgeGate,
  isNativePlatform,
  readAttestation,
  writeAttestation,
  readDeclined,
  writeDeclined,
} from '@/lib/ageGate';

export function AgeGate() {
  const { user, isLoading } = useAuth();
  const { pathname } = useLocation();
  const [attestedNow, setAttestedNow] = useState(false);
  const [declined, setDeclined] = useState(() => readDeclined());

  const show = !attestedNow && shouldShowAgeGate({
    isNative: isNativePlatform(),
    isAuthenticated: !!user,
    authLoading: isLoading,
    storedAttestation: readAttestation(),
    now: Date.now(),
    pathname,
  });

  if (!show) return null;

  // Failure state: full-screen block, session-only (a fresh session may re-answer).
  if (declined) {
    return (
      <div className="fixed inset-0 z-[100] bg-background flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <ShieldAlert className="h-10 w-10 text-damage mx-auto" />
          <h1 className="text-xl font-display font-bold text-foreground">
            Sabotage Fantasy is for adults only
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            You must be 18 or older (19+ in Nebraska) to use Sabotage Fantasy.
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed">
            If you or someone you know has a gambling problem, help is available: call or text{' '}
            <b className="text-foreground">1-800-GAMBLER</b> or visit{' '}
            <a
              href="https://www.ncpgambling.org"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-foreground hover:text-primary"
            >
              ncpgambling.org
            </a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 space-y-4 text-center">
        <img src="/images/logo-topbar@2x.png" alt="Sabotage Fantasy" className="h-8 mx-auto" />
        <h1 className="text-lg font-display font-bold text-foreground">
          You must be 18 or older to play
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Sabotage Fantasy is a real-money fantasy sports platform for adults 18+
          (19+ in Nebraska). Are you of legal age?
        </p>
        <div className="flex flex-col gap-2">
          <Button
            onClick={() => { writeAttestation(Date.now()); setAttestedNow(true); }}
            className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
          >
            I am 18 or older
          </Button>
          <Button
            onClick={() => { writeDeclined(); setDeclined(true); }}
            variant="outline"
            className="w-full h-11 border-border text-muted-foreground"
          >
            No
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          See our{' '}
          <a href="/responsible-gaming" className="underline hover:text-primary">Responsible Gaming</a>
          {' '}and{' '}
          <a href="/state-availability" className="underline hover:text-primary">eligibility</a>
          {' '}policies.
        </p>
      </div>
    </div>
  );
}
